// src/components/MyTeams.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  Box,
} from "@mui/material";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  arrayRemove,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebase-config";
import { useAuth } from "../../context/AuthContext";

// Reuse the same UserInfo component to display detailed user info
const UserInfo = ({ uid, isCreator = false }) => {
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userDocRef = doc(db, "users", uid);
        const docSnap = await getDoc(userDocRef);
        if (docSnap.exists()) {
          setUserData(docSnap.data());
        } else {
          setUserData(null);
        }
      } catch (error) {
        console.error("Error fetching user info", error);
      }
    };
    fetchUser();
  }, [uid]);

  if (!userData) return <span>Loading...</span>;

  return (
    <Box>
      <Typography variant="subtitle1">
        {userData.firstName} {userData.lastName} {isCreator ? "(Creator)" : ""}
      </Typography>
      <Typography variant="body2">
        Handicap: {userData.handicap || "N/A"}
      </Typography>
      <Typography variant="body2">
        Location: {userData.location || "N/A"}
      </Typography>
      <Typography variant="body2">
        Region: {userData.region || "N/A"}
      </Typography>
    </Box>
  );
};

// Simple MemberCard for teams you're a member of
const MemberCard = ({ uid, isCreator }) => {
  return (
    <Card variant="outlined" sx={{ mb: 1, p: 1 }}>
      <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
        <UserInfo uid={uid} isCreator={isCreator} />
      </CardContent>
    </Card>
  );
};

const MyTeams = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  // Fetch teams where the user is a member but not the creator.
  const fetchMyTeams = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const teamsSnap = await getDocs(collection(db, "teams"));
      const memberTeams = teamsSnap.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .filter(
          (team) =>
            team.members &&
            team.members.includes(user.uid) &&
            team.createdBy !== user.uid
        );
      setTeams(memberTeams);
    } catch (err) {
      console.error(err);
      setError("Failed to load your teams.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchMyTeams();
    }
  }, [user, fetchMyTeams]);

  // Allow a member to leave a team.
  const handleLeaveTeam = async (teamId) => {
    setError("");
    setMessage("");
    try {
      const teamDocRef = doc(db, "teams", teamId);
      await updateDoc(teamDocRef, {
        members: arrayRemove(user.uid),
      });
      setMessage("You have left the team.");
      fetchMyTeams();
    } catch (err) {
      console.error(err);
      setError("Failed to leave the team.");
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}
      {loading ? (
        <Typography>Loading teams...</Typography>
      ) : teams.length === 0 ? (
        <Typography>
          You are not a member of any teams (excluding teams you created).
        </Typography>
      ) : (
        teams.map((team) => (
          <Card key={team.id} sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6">{team.teamName}</Typography>
              <Typography variant="body2">
                Description: {team.description || "N/A"}
              </Typography>
              <Typography variant="body2">
                Members: {team.members ? team.members.length : 0}
              </Typography>
              {team.members && team.members.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2">Team Members:</Typography>
                  {team.members.map((memberId) => (
                    <MemberCard
                      key={memberId}
                      uid={memberId}
                      isCreator={memberId === team.createdBy}
                    />
                  ))}
                </Box>
              )}
              <Button
                variant="contained"
                color="secondary"
                onClick={() => handleLeaveTeam(team.id)}
                sx={{ mt: 2 }}
              >
                Leave Team
              </Button>
            </CardContent>
          </Card>
        ))
      )}
    </Container>
  );
};

export default MyTeams;